import React from "react";
import { DndContext, pointerWithin, DragOverlay } from "@dnd-kit/core";
import {
	SortableContext,
	verticalListSortingStrategy,
	useSortable,
	arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import FontAwesome from "react-fontawesome";
import { RowGripIcon } from "../Info/components/RowGripIcon";
import { RowActionsMenu } from "../Info/components/RowActionsMenu";
import { TEXT_LIGHT } from "../../js";

function SortableCategoryItem({ category, onEdit, onDelete }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: category.RowId });

	const style = {
		opacity: isDragging ? 0.85 : 1,
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const actionItems = [
		{
			label: "Edit category",
			icon: "pencil",
			onSelect: () => onEdit(category),
		},
		{
			label: "Delete category",
			icon: "trash",
			onSelect: () => onDelete(category),
		},
	];

	return (
		<div ref={setNodeRef} style={style} className="infopage__row">
			<div className="row__drag-handle" {...listeners} {...attributes}>
				<RowGripIcon />
			</div>
			<div className="row__content">
				<div className="row__primary">
					<FontAwesome name="tag" className="row__type-icon" />
					<span className="row__title">{category.Name}</span>
				</div>
			</div>
			<RowActionsMenu
				label={`More actions for ${category.Name}`}
				items={actionItems}
			/>
		</div>
	);
}

function DragOverlayItem({ category }) {
	if (!category) return null;
	return (
		<div className="infopage__row--overlay">
			<div className="row__drag-handle">
				<RowGripIcon />
			</div>
			<div className="row__content">
				<div className="row__primary">
					<FontAwesome name="tag" className="row__type-icon" />
					<span className="row__title">{category.Name}</span>
				</div>
			</div>
		</div>
	);
}

function ServiceCategoriesList({ categories, onEdit, onDelete, onReorder }) {
	const [activeId, setActiveId] = React.useState(null);

	const handleDragStart = (event) => {
		setActiveId(event.active.id);
	};

	const handleDragEnd = (event) => {
		setActiveId(null);
		const { active, over } = event;
		if (!over || active.id === over.id) {
			return;
		}
		const oldIndex = categories.findIndex((c) => c.RowId === active.id);
		const newIndex = categories.findIndex((c) => c.RowId === over.id);
		if (oldIndex === -1 || newIndex === -1) {
			return;
		}
		const reordered = arrayMove(categories, oldIndex, newIndex);
		onReorder(reordered);
	};

	const handleDragCancel = () => {
		setActiveId(null);
	};

	if (!categories || categories.length === 0) {
		return (
			<div style={styles.emptyState}>
				<span style={{ color: TEXT_LIGHT, fontSize: "medium" }}>
					No categories yet
				</span>
			</div>
		);
	}

	const itemIds = categories.map((c) => c.RowId);
	const activeCategory = activeId
		? categories.find((c) => c.RowId === activeId)
		: null;

	return (
		<DndContext
			collisionDetection={pointerWithin}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
		>
			<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
				<div className="infopage__rows">
					{categories.map((category) => (
						<SortableCategoryItem
							key={category.RowId}
							category={category}
							onEdit={onEdit}
							onDelete={onDelete}
						/>
					))}
				</div>
			</SortableContext>
			<DragOverlay dropAnimation={null}>
				<DragOverlayItem category={activeCategory} />
			</DragOverlay>
		</DndContext>
	);
}

const styles = {
	emptyState: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: 32,
	},
};

export default ServiceCategoriesList;
